import * as EmailValidator from "email-validator";
import { useState } from "react";
import { UnmountClosed } from "react-collapse";

import "../styles.scss";

function Project({
  title,
  subtitle,
  mobileSubtitle,
  sidebar,
  url,
  active,
  imageName,
  imagePosition,
  disabled,
  extension
}) {
  return (
    <li className={`${disabled ? "Disabled" : ""} ${active ? "Active" : ""}`}>
      <a href={url}>
        <h3 dangerouslySetInnerHTML={{ __html: title }} />
        <p wideonly={mobileSubtitle !== undefined ? "true" : "false"}>
          {subtitle}
        </p>
        {mobileSubtitle !== undefined && (
          <p narrowonly="true">{mobileSubtitle}</p>
        )}
      </a>

      <style jsx>{`
        li {
          background-image: url(/static/images/${imageName}${sidebar
              ? "-mobile"
              : ""}.${extension});
          background-position: ${imagePosition || "center left"};
          background-size: cover;
        }
        @media (max-width: 1023px) {
          li {
            background-image: url(/static/images/${imageName}-mobile.${extension});
          }
        }
      `}</style>
    </li>
  );
}

function Notes({ label, mobileLabel, isStandalone, children }) {
  return (
    <div className={`Notes ${isStandalone ? "Standalone" : ""}`}>
      {mobileLabel && <h2 className="narrowOnly">{mobileLabel}</h2>}
      {label !== null && (
        <h2 className={mobileLabel ? "wideOnly" : ""}>{label || "Notes"}</h2>
      )}
      <ol className={`Notes ${label === null ? "NoLabel" : ""}`}>{children}</ol>
    </div>
  );
}

function Note({ title, subtitle, url, active, disabled, locked }) {
  return (
    <li className={`${active ? "Active" : ""} ${disabled ? "Disabled" : ""}`}>
      <a href={url}>
        {locked && <img className="Locked" src="/static/images/lock.svg" />}
        <h3>{title}</h3>
        <p>{subtitle}</p>
      </a>
    </li>
  );
}

export function Controls() {
  const [emailAddress, setEmailAddress] = useState("");
  return (
    <div className="Controls">
      <div className="LinkContainer">
        <a href="mailto:andy@andymatuschak.org">Email</a>
        <a href="https://twitter.com/andy_matuschak">Twitter</a>
      </div>
      <div className="MailingListContainer">
        <label htmlFor="email">Want to hear when I publish new work?</label>
        <div className="FormContainer">
          <form
            action="https://mailinglist.andymatuschak.org/api/subscribe"
            method="post"
            target="popupwindow"
            onSubmit={() => {
              window.open("", "popupwindow", "width=400,height=400");
            }}
          >
            <input type="hidden" value="1" name="embed" />
            <input
              type="text"
              name="email"
              placeholder="Your email, please"
              size="1"
              onChange={event => setEmailAddress(event.target.value)}
              value={emailAddress}
            />
            <button
              type="submit"
              disabled={!emailAddress || !EmailValidator.validate(emailAddress)}
            >
              Subscribe
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

function Section({ children, ...otherProps }) {
  return (
    <section {...otherProps}>
      <div className="Inner">{children}</div>
    </section>
  );
}

export default ({ sidebar }) => {
  const [isExpanded, setExpanded] = React.useState(false);
  const readMore = React.useCallback(() => setExpanded(true));

  const metaExpandedContent = (
    <>
      <p>I believe personal computers can enable <a href="https://numinous.productions/ttft/">transformative tools for
        thought</a>: environments that radically transform what people can think and do, so much so that we expand the
        set of thoughts it’s possible to think. I want to produce <em>alien cognitive and creative powers</em>—as
        wondrous and magical to us today as a modern visual effects artist might seem to a cave painter.</p>
      <p>
        I&rsquo;m an independent researcher, supported by a crowdfunded
        grant from my{" "}
        <a href="https://www.patreon.com/quantumcountry">Patreon</a> community.
        If you find my work interesting, you can{" "}
        <a href="https://www.patreon.com/quantumcountry">become a member</a> to help make it happen. You&rsquo;ll
        get regular essays on work-in-progress, access to events, previews of new prototypes, and more.
      </p>
      <p>
        Prior to my current work, I helped build iOS at Apple and led{" "}
        <a href="https://early.khanacademy.org">R&D at Khan Academy</a>.
      </p>
    </>
  );

  return (
    <div className={`SectionList ${sidebar ? "Sidebar" : "NotSidebar"}`}>
      <div className="BeginningBGExtender"/>
      <section className="Meta">
        <div className="MetaContent Inner">
          <div className="NameAndButtons">
            <h1 className="wideOnly">
              <a href="/">
                Andy
                <br/>
                Matuschak
              </a>
            </h1>
            <h1 className="narrowOnly">
            <a href="/">Andy Matuschak</a>
            </h1>
            <div className="HeaderButtons narrowOnly">
              <a href="mailto:andy@andymatuschak.org">Email</a>
              <a href="https://twitter.com/andy_matuschak">Twitter</a>
            </div>
          </div>
          <div className="narrowOnly">
            <p>
              I'm an applied researcher, focused on creating user interfaces that expand what people can think and do.{" "}
              <button
                onClick={readMore}
                style={{ opacity: isExpanded ? 0 : 1 }}
              >
                more…
              </button>
            </p>
            <UnmountClosed isOpened={isExpanded}>
              <p>My current work centers on reading environments which actively help people understand, remember, and use what they read.</p>
              {metaExpandedContent}
            </UnmountClosed>
          </div>
          <div className="wideOnly">
            <p>
              I'm an applied researcher, focused on creating user interfaces that expand what people can think and do. My current focus is an augmented book which actively helps people understand, remember, and use what they read.
            </p>
            {metaExpandedContent}
          </div>
        </div>
        {!sidebar && (
          <div className="wideOnly" style={{ flexShrink: 0 }}>
            <Controls />
          </div>
        )}
      </section>

      <Section>
        <header>
          <h1 className="wideOnly">
            Projects:
            <br />
            tools for thought
          </h1>
          <h1 className="narrowOnly">Projects: tools for thought</h1>
          <p className="wideOnly">
            Augmenting cognition and creativity; "how to create magicians?";
            "what comes after the book?"; enabling environments.
          </p>
          <p className="narrowOnly">What comes after the book?</p>
        </header>
        <ol className="MajorProjects">
          <Project
            title={`How might we learn?`}
            subtitle="Towards convivial AI-augmented learning"
            sidebar={sidebar}
            url="/hmwl"
            imageName="hmwl"
            imagePosition="center center"
            extension="svg"
          />
          <Project
            title={`How can we develop transformative tools for thought?`}
            subtitle="Barriers and opportunities, joint with Michael Nielsen"
            mobileSubtitle=""
            sidebar={sidebar}
            url="https://numinous.productions/ttft"
            imageName="ttft"
            imagePosition="center center"
            extension="png"
          />
          <Project
            title={`<span class="alternateGlyph">Q</span>uantum Country`}
            subtitle="An experimental &ldquo;mnemonic book,&rdquo; joint with Michael Nielsen"
            sidebar={sidebar}
            url="https://quantum.country"
            imageName="quantum-country"
            imagePosition="center left"
            extension="svg"
          />
          <Project
            title="Why books don&rsquo;t work"
            subtitle="On designing media to reflect how people think and learn"
            sidebar={sidebar}
            url="/books"
            imageName="books"
            imagePosition="center center"
            extension="png"
          />
          <Project
            title="Timeful texts"
            subtitle="Extending the reading experience over time; joint with Michael Nielsen"
            sidebar={sidebar}
            url="https://numinous.productions/timeful"
            imageName="timeful"
            imagePosition="center center"
            extension="svg"
          />
        </ol>
        <Notes label="More work" mobileLabel="More">
          <Note
            title="Orbit"
            subtitle="A platform for memory system experiments"
            url="https://withorbit.com"
          />
          <Note
            title="How to write good prompts"
            subtitle="Using spaced repetition to create understanding"
            url="https://andymatuschak.org/prompts"
          />
        </Notes>
        <Notes label="Working notes" mobileLabel="Notes">
          <Note
            title="What&lsquo;s top of mind"
            subtitle="An index of the questions and ideas I&lsquo;m exploring"
            url="https://notes.andymatuschak.org/About_these_notes?stackedNotes=zUw5PuD8op9oq8kHvni6sug6eRTNtR9Wqma"
          />
          <Note
            title="The mnemonic medium"
            subtitle="Design properties, evidence of impact, top questions"
            url="https://notes.andymatuschak.org/z4rRX3qwSSJRsEkdXKwH2shamgHNeRthrMLiF"
          />
          <Note
            title="Evergreen notes"
            subtitle="Methods for developing ideas over time"
            url="https://notes.andymatuschak.org/z4SDCZQeRo4xFEQ8H4qrSqd68ucpgE6LU155C"
          />
          <Note
            title="Enabling environments, video games, and the Primer"
            subtitle="Incomplete notes on a new kind of growth environment"
            url="https://notes.andymatuschak.org/z244xx3kMf1v8UnkZKUwQBAwBsHLpUgN4YhsG"
          />
          <Note
            title="Programmable attention"
            subtitle="Systems which systematically orchestrate your mind"
            url="https://notes.andymatuschak.org/zJrfPCbY7GcpV9asEc8NTVzXTAV4TvRFMuY6"
          />
          <Note
            title="Taking knowledge work seriously"
            subtitle="Athletes and musicians vs. knowledge workers"
            url="https://notes.andymatuschak.org/z5opHsGrNmCib7YQfLv6XbYURzZgZmx4Mrh5y"
          />
        </Notes>
      </Section>

      <Section>
        <header>
          <h1 className="wideOnly">
            Letters from
            <br />
            the lab
          </h1>
          <h1 className="narrowOnly">Letters from the lab</h1>
          <p className="wideOnly">
            Monthly essays on work in progress. Most are initially written for
            my <a href="https://www.patreon.com/quantumcountry">patrons</a>, but
            some are later made available to the public.
          </p>
          <p className="narrowOnly">
            Less formal essays (some{" "}
            <a href="https://www.patreon.com/quantumcountry">patron</a>-only)
          </p>
        </header>
        <Notes label={null} isStandalone>
          <Note
            title="Working with blips: our second system"
            subtitle="A year of augmented attention work"
            url="https://www.patreon.com/posts/137914548"
            locked
          />
          <Note
            title="Augmenting scholarship: a proto-proposal"
            subtitle="Towards dynamic media for reading and synthesis"
            url="https://www.patreon.com/posts/135440922"
            locked
          />
          <Note
            title="Preparatory notes for a manifesto"
            subtitle="Some core beliefs behind my work"
            url="https://www.patreon.com/posts/130320045"
            locked
          />
          <Note
            title="A malleable reading environment"
            subtitle="Towards an improvisatory digital reading environment"
            url="https://www.patreon.com/posts/127847642"
            locked
          />
          <Note
            title="A startling glimpse of malleable software"
            subtitle="Case study for an AI-mediated software ecosystem"
            url="https://www.patreon.com/posts/125526090"
            locked
          />
          <Note
            title="Tools for tools for thought"
            subtitle="Some principles for inventing tools for thought"
            url="https://www.patreon.com/posts/123313625"
            locked
          />
          <Note
            title="Design-in-action: book markup grid"
            subtitle="A look inside my design process"
            url="https://www.patreon.com/posts/121250335"
            locked
          />
          <Note
            title="Invention, influence, and impact"
            subtitle="Notes on my theory of change"
            url="https://www.patreon.com/posts/119062444"
            locked
          />
          <Note
            title="A primitive for enabling environments"
            subtitle="Early work on machine-generated prompts"
            url="https://www.patreon.com/posts/116921064"
            locked
          />
          <Note
            title="What can malleable software learn from Realtalk?"
            subtitle="Connecting two dreams of software liberation"
            url="https://www.patreon.com/posts/113122332"
            locked
          />
          <Note
            title="On sitting down to study independently"
            subtitle="A catalog of problems for self-motivated students"
            url="https://www.patreon.com/posts/111164399"
            locked
          />
          <Note
            title="Five years of evergreen notes"
            subtitle="Some long-term reflections on networked notes"
            url="https://www.patreon.com/posts/109216672"
            locked
          />
          <Note
            title="Exorcising us of the Primer"
            subtitle="Transcending The Diamond Age's vision for learning"
            url="/primer"
            />
          <Note
            title="A spring flood of projects"
            subtitle="April '24 updates on Orbit, BookBridge, etc"
            url="https://www.patreon.com/posts/spring-flood-of-103393573"
            locked
            />
          <Note
            title="What's worth learning if we have AGI?"
            subtitle="What kinds of knowledge remain meaningful?"
            url="/worth-learning-agi"
          />
          <Note
          title="What does spatial computing want to become?"
          subtitle="Pouring the silicon into the concrete"
          url="https://www.patreon.com/posts/what-does-want-99504553"
          locked
          />
          <Note
            title="In praise of the particular"
            subtitle="…and other lessons from 2023"
            url="/2023"
          />
          <Note
            title="Initial results from highlight-driven prototype"
            subtitle="Observations from working with 14 students"
            url="https://www.patreon.com/posts/initial-results-93831375"
            locked
          />
          <Note
            title="On breadth vs. depth in learning"
            subtitle="How to distribute time in study?"
            url="https://www.patreon.com/posts/on-breadth-vs-in-91720220"
            locked
          />
          <Note
            title="Highlight-driven practice/comprehension support"
            subtitle="A new reading augmentation design concept"
            url="https://www.patreon.com/posts/highlight-driven-90101210"
            locked
          />
          <Note
            title="Studying myself studying linear algebra"
            subtitle="Some problems with problem-solving practice"
            url="https://www.patreon.com/posts/studying-myself-88583610"
            locked
          />
          <Note
            title="Initial experiments in self-explanation support"
            subtitle="Painting a text with explanation"
            url="https://www.patreon.com/posts/initial-in-self-87053505"
            locked
          />
          <Note
            title="Reading comprehension and memory systems"
            subtitle="Memory failures are often not the root problem"
            url="https://www.patreon.com/posts/reading-and-85345515"
            locked
          />
          <Note
            title="Fluid practice for fluid understanding"
            subtitle="Reframing review sessions as practice sessions"
            url="https://www.patreon.com/posts/fluid-practice-83882597"
            locked
          />
          <Note
            title="Ethics of AI-based invention: a personal inquiry"
            subtitle="Grappling with my moral obligations as a designer"
            url="/personal-ai-ethics"
          />
          <Note
            title="Memory systems and problem-solving practice"
            subtitle="Difficulties in knowledge transfer"
            url="https://www.patreon.com/posts/memory-systems-80865178"
            locked
          />
          <Note
            title="Becoming a Wizard-of-Oz learning assistant"
            subtitle="Field work experiments in adult learning"
            url="/woz-learning"
          />
          <Note
            title="Three years of crowdfunded research"
            subtitle="Reflections on my membership program"
            url="/2022"
          />
          <Note
            title="Towards impact through intimacy"
            subtitle="Tentative plans for my 2023 research"
            url="/impact-through-intimacy"
          />
          <Note
            title="Cultivating depth and stillness in research"
            subtitle="Notes on whatʼs worked for me"
            url="/stillness"
          />
          <Note
            title="Lessons from summer 2022 prototype"
            subtitle="Synthesis on new mnemonic medium"
            url="https://www.patreon.com/posts/lessons-from-73309142"
            locked
          />
          <Note
            title="Breaking the mnemonic medium out of its box"
            subtitle="Demo/talk of a new floating design"
            url="https://www.patreon.com/posts/71081197"
            locked
          />
          <Note
            title="Prospects for consumer silent speech interfaces"
            subtitle="Systems for dictation without audible sound"
            url="https://www.patreon.com/posts/prospects-for-69855805"
            locked
          />
          <Note
            title="The joyful surprises of user observation"
            subtitle="Notes from testing a new mnemonic medium"
            url="https://www.patreon.com/posts/joyful-surprises-68479856"
            locked
          />
          <Note
            title="A peritextual mnemonic medium"
            subtitle="Demo/talk of a redesign around reader control"
            url="https://www.patreon.com/posts/66348634"
            locked
          />
          <Note
            title="Implicit practice: a sight reading parable"
            subtitle="One way implicit practice fails in knowledge work"
            url="/sight-reading"
          />
          <Note
            title="Exponentials and forgetting in Quantum Country"
            subtitle="Patterns in preliminary data"
            url="https://www.patreon.com/posts/exponentials-and-63206775"
            locked
          />
          <Note
            title="Lessons from 2021"
            subtitle="Reflections on creative work, the field, crowdfunding"
            url="/2021"
          />
          <Note
            title="Tools for thought: science, design, art, craftsmanship?"
            subtitle="Lenses for progress in interface invention"
            url="/sdac"
          />
          <Note
            title="Quantum Country’s suspiciously flat forgetting curves"
            subtitle="Surprises in analysis of conceptual memory"
            url="https://www.patreon.com/posts/quantum-countrys-59365357"
            locked
          />
          <Note
            title="Doing-centric explanatory mediums"
            subtitle="Board game instruction manuals… and Figma"
            url="/doing-centric"
          />
          <Note
            title="Architectures for a more flexible mnemonic medium"
            subtitle="Interface approaches for reader control"
            url="https://www.patreon.com/posts/architectures-56842917"
            locked
          />
          <Note
            title="Revamping the mnemonic medium for reader control"
            subtitle="Expanding beyond primers' assumptions"
            url="https://www.patreon.com/posts/revamping-medium-55309960"
            locked
          />
          <Note
            title="Armories for tool-maker/-user collaborations"
            subtitle="Notes on bridging skills and ideas"
            url="https://www.patreon.com/posts/armories-for-52549432"
          />
          <Note
            title="Finding research–context fit"
            subtitle="A challenge for tool-builders"
            url="https://www.patreon.com/posts/finding-research-51501925"
          />
          <Note
            title="Crowdfunded research vs. the NSF CAREER grant"
            subtitle="Open-sourcing Orbit; technical collaborations"
            url="https://www.youtube.com/watch?v=8kB4IgqGO7k"
          />
          <Note
            title="Too easy to be effortless"
            subtitle="Surprising distributions in Quantum Country"
            url="https://www.patreon.com/posts/too-easy-to-be-49250309"
            locked
          />
          <Note
            title="Ratcheting progress in tools for thought"
            subtitle="How to accrete insight as a field?"
            url="https://www.patreon.com/posts/ratcheting-in-47976114"
          />
          <Note
            title="In search of better questions"
            subtitle="From memory systems to meaning"
            url="https://www.patreon.com/posts/in-search-of-47047644"
          />
          <Note
            title="Reflections on 2020 as an independent researcher"
            subtitle="Funding, culture, limitations, process"
            url="/2020"
          />
          <Note
            title="Liquid olives and iPhones"
            subtitle="Problem-solving and -finding; The Uncertainty Mindset"
            url="https://www.patreon.com/posts/liquid-olives-43912546"
          />
          <Note
            title="Working with authors: entangled skills"
            subtitle="Text-writing needs prompt-writing needs text-writing"
            url="https://www.patreon.com/posts/working-with-43630252"
            locked
          />
          <Note
            title="The carrying capacity of a regular memory practice"
            subtitle="Tensions with deliberate practice and flow"
            url="https://www.patreon.com/posts/carrying-of-and-42201887"
            locked
          />
          <Note
            title="The galaxy brain problem; speed-running UIs"
            subtitle="Challenges in explaining new mediums"
            url="https://www.patreon.com/posts/galaxy-brain-uis-41516298"
            locked
          />
          <Note
            title="“Skip”: exponential-backoff deferral mechanisms…"
            subtitle="… and fuzzy inboxes"
            url="https://www.patreon.com/posts/skip-exponential-40672377"
          />
          <Note
            title="Thoughts on crowdfunding tools for thought"
            subtitle="Remarks on a milestone in crowdfunding"
            url="https://www.patreon.com/posts/celebrating-on-40134847"
            locked
          />
          <Note
            title="A nascent art direction for Orbit"
            subtitle="Escaping educational aesthetics"
            url="https://www.patreon.com/posts/nascent-art-for-39070865"
            locked
          />
          <Note
            title="Demonstrating a personal mnemonic medium"
            subtitle="Integrating spaced repetition into prose notes"
            url="https://www.patreon.com/posts/demonstrating-38097140"
            locked
          />
          <Note
            title="Bringing ideas into your Orbit"
            subtitle="Extending the desktop for programmable attention"
            url="https://www.patreon.com/posts/bringing-ideas-36925173"
          />
        </Notes>
        <ol className="MajorProjects"></ol>
      </Section>

      <Section>
        <header>
          <h1 className="wideOnly">
            Projects:
            <br />
            ed-tech
          </h1>
          <h1 className="narrowOnly">Projects: experimental ed-tech</h1>
          <p className="wideOnly">
            Work from my time at{" "}
            <a href="https://khanacademy.org">Khan Academy</a>, where{" "}
            <a href="https://early.khanacademy.org">my team</a> worked with
            teachers to invent novel interactive learning environments.
          </p>
          <p className="narrowOnly">
            Creations from my R&D work at{" "}
            <a href="https://khanacademy.org">Khan Academy</a>.
          </p>
        </header>
        <ol className="MajorProjects">
          <Project
            title="Building complex skills online"
            subtitle="Beyond right and wrong: scalable open-ended learning activities"
            sidebar={sidebar}
            url="https://early.khanacademy.org/open-ended"
            imageName="free-response"
            imagePosition="center right"
            extension="svg"
          />
          <Project
            title="Numbers at play"
            subtitle="Designing digital manipu&shy;latives to reveal numbers&rsquo; hidden properties"
            sidebar={sidebar}
            url="https://early.khanacademy.org/cantor"
            imageName="cantor"
            imagePosition="center right"
            extension="svg"
          />
          <Project
            title="Playful worlds of creative math"
            subtitle="Reframing early numeracy around adventure, wonder, and creativity"
            sidebar={sidebar}
            url="https://early.khanacademy.org/early-math"
            imageName="early-math"
            imagePosition="bottom right"
            extension="svg"
          />
        </ol>
        <Notes label="Research Blog" mobileLabel="Blog">
          <Note
            title="Narrated explorables: three mental models"
            subtitle="When author and learner share a canvas"
            url="https://medium.com/khan-academy-early-product-development/narrated-explorables-three-mental-models-e16e0d80e4c1"
          />
          <Note
            title="Students are the subjects of our sentences"
            subtitle="A syntactic trick to emphasize student agency"
            url="https://medium.com/khan-academy-early-product-development/the-student-is-the-most-important-subject-in-our-sentences-2aba3fc58279"
          />
          <Note
            title="Looking sideways at the design squiggle"
            subtitle="Reflections on the team&rsquo;s design process"
            url="https://medium.com/khan-academy-early-product-development/looking-sideways-at-the-design-squiggle-9bab49d1c2de"
          />
          <Note
            title="Mastery learning and creative tasks"
            subtitle="Mastery learning&rsquo;s limits for complex skills"
            url="https://medium.com/khan-academy-early-product-development/mastery-learning-and-creative-tasks-97c4a11f8364"
          />
          <Note
            title="Sorting product &ldquo;baseball cards&rdquo;"
            subtitle="Bridging behavioral interviews and prototypes"
            url="https://medium.com/khan-academy-early-product-development/sorting-product-baseball-cards-bridging-behavioral-interviews-and-prototypes-510057967cf"
          />
          <Note
            title="Explanations from &lsquo;masters&rsquo; of geometry"
            subtitle="The gulf between application and explanation"
            url="https://medium.com/khan-academy-early-product-development/explanations-from-alleged-masters-of-shape-dissection-8ebed962b2d9"
          />
          <Note
            title="Safely showing students peers&rsquo; comments"
            subtitle="Designing peer interactions to avoid abuse"
            url="https://medium.com/khan-academy-early-product-development/safely-showing-students-how-others-see-their-work-e6d779b2da2c"
          />
          <Note
            title="Games vs. education on unguided instruction"
            subtitle="The limits of teaching without words"
            url="https://klr.tumblr.com/post/159583871898/game-designers-vs-education-researchers-on"
          />
          <Note
            title="Rich tasks which crowdsource more rich tasks"
            subtitle="Using student work to arrange social learning"
            url="https://klr.tumblr.com/post/158036182833/rich-tasks-crowdsourcing-data-for-more-rich-tasks"
          />
          <Note
            title="Surveying the open-ended task landscape"
            subtitle="Notes from literature / market review"
            url="https://klr.tumblr.com/post/156063609578/surveying-the-open-ended-response-landscape"
          />
          <Note
            title="Amplifying open-ended questions"
            subtitle="Enabling open-ended online learning activities"
            url="https://klr.tumblr.com/post/153878272318/amplifying-open-ended-questions"
          />
          <Note
            title="One medium, many contexts"
            subtitle="Designing math manipulatives for fluid use"
            url="https://klr.tumblr.com/post/154133471528/listening-to-educators-listening-to-students"
          />
          <Note
            title="Old and new ways of looking at numbers"
            subtitle="Making abstract properties more visible"
            url="https://klr.tumblr.com/post/149677916243/old-and-new-ways-of-looking-at-numbers"
          />
          <Note
            title="A medium for exploring quantity"
            subtitle="Introducing our math manipulative project"
            url="https://klr.tumblr.com/post/147245626988/a-medium-supporting-the-exploration-of-quantities"
          />
          <Note
            title="Student agency and flow"
            subtitle="Helping students surf their learning edge"
            url="https://klr.tumblr.com/post/146190595653/student-agency-and-flow"
          />
        </Notes>
      </Section>

      <Section>
        <header>
          <h1>More, misc, meta</h1>
          <p className="wideOnly">
            Miscellaneous writing, appearances, and projects scattered across
            the web.
          </p>
          <p className="narrowOnly">
            Miscellaneous writing, appearances, projects.
          </p>
        </header>
        <ol className="MajorProjects">
          <Project
            title="Scrying Pen"
            subtitle="A pen which draws its own future using a realtime recurrent neural network"
            sidebar={sidebar}
            url="/scrying-pen"
            imageName="scrying-pen"
            imagePosition="center right"
            extension="svg"
          />
        </ol>
        <Notes
          label={"Selected podcasts"}
          mobileLabel={"Podcasts"}
          isStandalone
        >
          <Note
            title="The Lunar Society with Dwarkesh Patel"
            subtitle="2023-06-21"
            url="https://www.youtube.com/watch?v=dmeRQN9z504"
          />
          <Note
            title="The Jolly Swagman with Joe Walker"
            subtitle="2022-12-24"
            url="https://josephnoelwalker.com/141-intellectual-exoskeletons-andy-matuschak/"
          />
          <Note
            title="Econtalk with Russ Roberts"
            subtitle="2019-08-05"
            url="http://www.econtalk.org/andy-matuschak-on-books-and-learning/"
          />
          <Note
            title="Tools & Craft with Devon Zuegel"
            subtitle="2022-06-08"
            url="https://www.notion.so/blog/andy-matuschak"
          />
          <Note
            title="Clearer Thinking with Spencer Greenberg"
            subtitle="2021-07-29"
            url="https://clearerthinkingpodcast.com/?ep=063"
          />
          <Note
            title="Metamuse with Adam Wiggins and Mark McGranaghan"
            subtitle="2020-09-03"
            url="https://museapp.com/podcast/12-growing-ideas/"
          />
        </Notes>
        <Notes label={"Other writing"} mobileLabel={"Articles"} isStandalone>
          <Note
            title="Exalting data, missing meaning"
            subtitle="The delusions of measurement absent theory"
            url="https://blog.andymatuschak.org/post/159340765257/exalting-data-missing-meaning"
          />
          <Note
            title="Satisfaction and progress in open-ended work"
            subtitle="Seeking gradient in the fog of novel work"
            url="https://blog.andymatuschak.org/post/159979927467/satisfaction-and-progress-in-open-ended-work"
          />
          <Note
            title="Building habits via smoothly ratcheting targets"
            subtitle="A surprisingly reliable strategy for starting habits"
            url="https://blog.andymatuschak.org/post/169043084412/successful-habits-through-smoothly-ratcheting"
          />
        </Notes>
      </Section>

      <section className="narrowNonSidebarOnly">
        <Controls />
      </section>

      <div className="EndingBGExtender" />
    </div>
  );
};
